import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { BigFormModal } from "@/components/ui/big-form-modal";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Check, Image as ImageIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getPlaceholderPhotos, addPlaceholderPhotoToLocation } from "@/services/campPhotoService";

interface PlaceholderPhotoDialogProps {
  isOpen: boolean;
  onClose: () => void;
  locationId: string;
  onSuccess: () => void;
}

export const PlaceholderPhotoDialog: React.FC<PlaceholderPhotoDialogProps> = ({
  isOpen,
  onClose,
  locationId,
  onSuccess
}) => {
  const { currentUser } = useAuth();
  const [placeholderPhotos, setPlaceholderPhotos] = useState<string[]>([]);
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch placeholder photos when dialog opens
  useEffect(() => {
    if (isOpen) {
      fetchPlaceholderPhotos();
    }
  }, [isOpen]);

  const fetchPlaceholderPhotos = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log("PlaceholderPhotoDialog: Fetching placeholder photos");
      
      const photos = await getPlaceholderPhotos();
      setPlaceholderPhotos(photos);
      
      if (photos.length === 0) {
        setError("No placeholder photos available. Please contact your administrator.");
      }
    } catch (error) {
      console.error("PlaceholderPhotoDialog: Error fetching placeholder photos:", error);
      setError("Failed to load placeholder photos. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoSelect = (photoUrl: string) => {
    setSelectedPhoto(photoUrl);
  };

  const handleSetAsHeader = async () => {
    if (!selectedPhoto || !currentUser) {
      toast.error("Please select a photo and ensure you're logged in");
      return;
    }

    try {
      setSaving(true);
      console.log("PlaceholderPhotoDialog: Setting placeholder photo as header");
      
      await addPlaceholderPhotoToLocation(
        locationId,
        currentUser.uid,
        selectedPhoto
      );
      
      toast.success("Placeholder photo added successfully");
      onSuccess();
      onClose();
    } catch (error) {
      console.error("PlaceholderPhotoDialog: Error adding placeholder photo:", error);
      toast.error("Failed to add placeholder photo");
    } finally {
      setSaving(false);
    }
  };

  const renderLoadingState = () => (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-4"></div>
      <p className="text-sm text-gray-500">Loading placeholder photos...</p>
    </div>
  );

  const renderErrorState = () => (
    <div className="flex flex-col items-center justify-center py-12">
      <ImageIcon className="h-12 w-12 text-gray-400 mb-4" />
      <p className="text-sm text-gray-600 text-center max-w-md">{error}</p>
      <Button 
        variant="outline" 
        onClick={fetchPlaceholderPhotos}
        className="mt-4"
      >
        Try again
      </Button>
    </div>
  );

  const renderPhotoGrid = () => (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {placeholderPhotos.map((photoUrl, index) => (
        <Card 
          key={index}
          className={`cursor-pointer transition-all hover:shadow-lg ${
            selectedPhoto === photoUrl 
              ? 'ring-2 ring-blue-500 shadow-lg' 
              : 'hover:ring-1 hover:ring-gray-300'
          }`}
          onClick={() => handlePhotoSelect(photoUrl)}
        >
          <div className="relative">
            <AspectRatio ratio={16/9}>
              <img 
                src={photoUrl} 
                alt={`Placeholder photo ${index + 1}`}
                className="object-cover w-full h-full rounded-t-md"
                onError={(e) => {
                  console.error("Failed to load placeholder image:", photoUrl);
                  e.currentTarget.style.display = 'none';
                }}
              />
            </AspectRatio>
            {selectedPhoto === photoUrl && (
              <div className="absolute top-2 right-2 bg-blue-500 text-white rounded-full p-1">
                <Check className="h-4 w-4" />
              </div>
            )}
          </div>
          <div className="p-3">
            <p className="text-xs text-gray-500 text-center">
              Photo {index + 1}
            </p>
          </div>
        </Card>
      ))}
    </div>
  );

  const renderContent = () => {
    if (loading) return renderLoadingState();
    if (error) return renderErrorState();
    if (placeholderPhotos.length === 0) return renderErrorState();
    return renderPhotoGrid();
  };

  return (
    <BigFormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Add placeholder photo"
      saveLabel="Set as header"
      isSaving={saving}
      onSave={handleSetAsHeader}
      isDirty={selectedPhoto !== null}
      description={
        <p className="text-sm text-gray-600">
          Choose a placeholder photo to quickly add as your location's header image. 
          You can edit or replace it later.
        </p>
      }
    >
      <div className="space-y-6">
        {renderContent()}
        
        {selectedPhoto && !loading && !error && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <p className="text-sm text-blue-800">
              <strong>Selected:</strong> This photo will be added as your location's header image 
              with permission status "granted" and photo credit "from Canva".
            </p>
          </div>
        )}
      </div>
    </BigFormModal>
  );
}; 